import {useEffect, useMemo, useRef, useState} from "react";
import {CheckNamePoVinsResponse} from "../../../shared/entities/Order/CheckNamePoVins";
import {extractNamePoVins} from "../../../shared/utils/namePoVin";

export type RoVinConflictRow = { orderId: number; namePo: string };

type CheckNamePoVinsResult = {
    Some: CheckNamePoVinsResponse;
    None?: unknown;
};

type Params = {
    namePo: string;
    selectedDealerId?: number;
    isViewWithPath: boolean;
    checkNamePoVins: (dealershipId: number, namePo: string) => Promise<CheckNamePoVinsResult>;
};

type RoVinConflictState = {
    conflicts: string[];
    conflictOrderIds: number[];
    conflictRows: RoVinConflictRow[];
    loading: boolean;
    modalOpen: boolean;
    acknowledgedNamePo: string | null;
};

const EMPTY_STATE: RoVinConflictState = {
    conflicts: [],
    conflictOrderIds: [],
    conflictRows: [],
    loading: false,
    modalOpen: false,
    acknowledgedNamePo: null,
};

export function useRoVinConflictCheck({
    namePo,
    selectedDealerId,
    isViewWithPath,
    checkNamePoVins,
}: Params) {
    const [state, setState] = useState<RoVinConflictState>(EMPTY_STATE);
    const dealershipIdRef = useRef<number | undefined>(undefined);
    const runIdRef = useRef(0);
    const ackRef = useRef<string | null>(null);
    const checkNamePoVinsRef = useRef(checkNamePoVins);
    checkNamePoVinsRef.current = checkNamePoVins;

    const detectedVins = useMemo(() => extractNamePoVins(namePo), [namePo]);
    const ackedForCurrentPo = state.acknowledgedNamePo !== null && state.acknowledgedNamePo === namePo.trim();
    const shouldBlockCheckout =
        !isViewWithPath &&
        detectedVins.length > 0 &&
        (state.loading || (state.conflictOrderIds.length > 0 && !ackedForCurrentPo));

    const clearConflictResult = (withLoading: boolean) => {
        setState((prev) => ({
            ...prev,
            conflicts: [],
            conflictOrderIds: [],
            conflictRows: [],
            loading: withLoading,
            modalOpen: false,
        }));
    };

    const acknowledgeCurrentPo = () => {
        const trimmed = namePo.trim();
        ackRef.current = trimmed;
        setState((prev) => ({...prev, acknowledgedNamePo: trimmed, modalOpen: false}));
    };

    const closeModal = () => {
        setState((prev) => ({...prev, modalOpen: false}));
    };

    useEffect(() => {
        if (selectedDealerId !== dealershipIdRef.current) {
            dealershipIdRef.current = selectedDealerId;
            ackRef.current = null;
            setState((prev) => ({...EMPTY_STATE, acknowledgedNamePo: null}));
            return;
        }

        if (isViewWithPath || !selectedDealerId) {
            clearConflictResult(false);
            return;
        }

        if (detectedVins.length === 0) {
            clearConflictResult(false);
            return;
        }

        const runId = ++runIdRef.current;
        const namePoSnapshot = namePo;
        const dealershipId = selectedDealerId;
        const timerId = window.setTimeout(() => {
            setState((prev) => ({...prev, loading: true}));
            checkNamePoVinsRef.current(dealershipId, namePoSnapshot).then((res) => {
                if (runId !== runIdRef.current) {
                    return;
                }

                if (res?.None || !res?.Some || typeof res.Some.allowed !== "boolean") {
                    clearConflictResult(false);
                    return;
                }

                const body = res.Some;
                const orderIds = body.conflictingOrderIds ?? [];
                const details = body.conflictingOrders;
                const rows: RoVinConflictRow[] =
                    details && details.length > 0
                        ? details.map((r) => ({
                            orderId: Number(r.orderId),
                            namePo: r.namePo ?? "",
                        }))
                        : orderIds.map((id) => ({orderId: id, namePo: ""}));
                const cVins = body.conflictingVins ?? [];

                if (!body.allowed && orderIds.length > 0) {
                    const isSameAck = ackRef.current === namePoSnapshot.trim();
                    setState((prev) => ({
                        ...prev,
                        loading: false,
                        conflicts: cVins,
                        conflictOrderIds: orderIds,
                        conflictRows: rows,
                        modalOpen: !isSameAck,
                    }));
                    return;
                }

                clearConflictResult(false);
            });
        }, 450);

        return () => window.clearTimeout(timerId);
    }, [namePo, selectedDealerId, isViewWithPath, detectedVins.length]);

    return {
        detectedVins,
        ackedForCurrentPo,
        shouldBlockCheckout,
        roVinConflicts: state.conflicts,
        roVinConflictOrderIds: state.conflictOrderIds,
        roVinConflictRows: state.conflictRows,
        roVinCheckLoading: state.loading,
        roVinModalOpen: state.modalOpen,
        acknowledgeCurrentPo,
        closeModal,
    };
}
